import { useState, type SVGProps } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useRegister } from '@/hooks/use-register'
import { useChurch } from '@/hooks/useChurch'
import { useChurchColors } from '@/hooks/useChurchColors'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, Mail, Lock, Sparkles, User, Phone, Eye, EyeOff } from 'lucide-react'

const registerSchema = z.object({
  name: z.string().min(3, 'Nome deve ter no mínimo 3 caracteres'),
  phone: z
    .string()
    .min(16, 'Telefone deve ter 11 dígitos')
    .regex(/^\(\d{2}\)\s\d\s\d{4}-\d{4}$/, 'Telefone inválido'),
  email: z.string().email('E-mail inválido'),
  password: z.string().min(6, 'Senha deve ter no mínimo 6 caracteres'),
})

type RegisterForm = z.infer<typeof registerSchema>

const GoogleIcon = ({ className, ...props }: SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className={className}
    {...props}
  >
    <path
      d="M21.35 11.1H12v3.8h5.3c-.23 1.2-.93 2.3-1.98 3.02l3.19 2.47c1.86-1.72 2.94-4.26 2.94-7.29 0-.73-.07-1.43-.19-2.1Z"
      fill="#4285F4"
    />
    <path
      d="M12 22c2.7 0 4.97-.9 6.63-2.42l-3.19-2.47c-.88.6-2 .96-3.44.96-2.65 0-4.9-1.79-5.7-4.19l-3.29 2.53C4.77 19.9 8.1 22 12 22Z"
      fill="#34A853"
    />
    <path
      d="M6.3 13.88c-.2-.6-.32-1.25-.32-1.88s.12-1.28.32-1.88L2.95 7.59A9.97 9.97 0 0 0 2 12a9.97 9.97 0 0 0 .95 4.41l3.35-2.53Z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.5c1.47 0 2.79.51 3.84 1.5l2.87-2.85C17 2.88 14.7 2 12 2 8.1 2 4.77 4.1 3.05 7.59l3.29 2.53C7.1 7.29 9.35 5.5 12 5.5Z"
      fill="#EA4335"
    />
  </svg>
)

// Apple social login removed per request

const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11)

  if (digits.length === 0) return ''
  if (digits.length <= 2) return `(${digits}`

  const areaCode = digits.slice(0, 2)
  const firstDigit = digits.slice(2, 3)
  const middlePart = digits.slice(3, 7)
  const lastPart = digits.slice(7, 11)

  if (digits.length <= 3) {
    return `(${areaCode}) ${firstDigit}`
  }

  if (digits.length <= 7) {
    return `(${areaCode}) ${firstDigit} ${digits.slice(3)}`
  }

  return `(${areaCode}) ${firstDigit} ${middlePart}-${lastPart}`
}

export function RegisterPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const { register: registerUser, isRegistering, registerWithSocial } = useRegister()
  const { church, currentLogo, hasLogo } = useChurch()
  const { primaryColor, secondaryColor } = useChurchColors()
  const [socialLoading, setSocialLoading] = useState<'google' | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
  })

  const phoneValue = watch('phone') ?? ''

  const handleSocialLogin = async (provider: 'google') => {
    try {
      setSocialLoading(provider)
      await registerWithSocial({ provider })
    } catch (error) {
      setError('Erro ao fazer registro com Google. Tente novamente.')
      console.error('Detalhes do erro:', error)
    } finally {
      setSocialLoading(null)
    }
  }

  const onSubmit = async (data: RegisterForm) => {
    try {
      setError(null)

      const result = await registerUser({
        name: data.name,
        email: data.email,
        phone: data.phone,
        password: data.password,
      })

      if (result.success) {
        navigate('/login', { 
          replace: true, 
          state: { 
            fromRegister: true, 
            registeredEmail: data.email 
          } 
        })
      } else {
        setError(result.error || 'Erro desconhecido no registro')
      }
    } catch {
      setError('Não foi possível concluir o cadastro. Tente novamente em instantes.')
    }
  }

  // Removed theme detection (Apple icon no longer used)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="relative"
    >
      <motion.div
        className="absolute inset-4 -z-10 rounded-3xl blur-2xl"
        style={{
          background: `linear-gradient(135deg, ${primaryColor}20, ${secondaryColor}10, hsl(var(--background))40)`
        }}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1, duration: 0.6 }}
      />
      <Card 
        className="overflow-hidden shadow-2xl backdrop-blur"
        style={{
          borderColor: `${primaryColor}33`,
          boxShadow: `0 25px 50px -12px ${primaryColor}33`
        }}
      >
        <div 
          className="h-2 w-full"
          style={{
            background: `linear-gradient(to right, ${primaryColor}, ${primaryColor}b3, ${secondaryColor})`
          }}
        />
        <CardHeader className="space-y-3 text-center">
          {/* Logo da Igreja - Livre, sem contorno */}
          {hasLogo && currentLogo && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="mx-auto"
            >
              <img 
                src={currentLogo} 
                alt={`Logo ${church?.nome || 'Igreja'}`}
                className="h-24 w-24 object-contain"
                onError={(e) => {
                  // Esconde a imagem se não carregar
                  e.currentTarget.style.display = 'none'
                }}
              />
            </motion.div>
          )}
          
          {/* Ícone padrão quando não há logo */}
          {!hasLogo && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl shadow-lg"
              style={{
                background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}b3)`,
                boxShadow: `0 10px 25px -5px ${primaryColor}33`
              }}
            >
              <Sparkles className="h-8 w-8 text-white" />
            </motion.div>
          )}
          
          <CardTitle className="text-2xl">Crie sua conta</CardTitle>
          <CardDescription>Informe seus dados para começar a usar o sistema</CardDescription>
        </CardHeader>
        <CardContent className="pb-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="space-y-3">
              <Button
                type="button"
                variant="outline"
                className="w-full shadow-sm"
                style={{
                  borderColor: primaryColor,
                  '--ring': primaryColor
                } as React.CSSProperties}
                onClick={() => handleSocialLogin('google')}
                disabled={isRegistering || socialLoading !== null}
              >
                {socialLoading === 'google' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Continuando com Google...
                  </>
                ) : (
                  <>
                    <GoogleIcon className="mr-2 h-4 w-4" />
                    Cadastrar com Google
                  </>
                )}
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-dashed border-muted" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-3 text-muted-foreground">ou use seus dados</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">
                Nome completo
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Ex.: Maria dos Santos"
                  {...register('name')}
                  disabled={isRegistering || socialLoading !== null}
                  className="pl-10"
                  style={{
                    '--ring': primaryColor
                  } as React.CSSProperties}
                />
              </div>
              {errors.name && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-destructive"
                >
                  {errors.name.message}
                </motion.p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium">
                Telefone
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(11) 99999-9999"
                  {...register('phone')}
                  value={phoneValue}
                  onChange={(event) => {
                    const formatted = formatPhone(event.target.value)
                    setValue('phone', formatted, { shouldDirty: true, shouldTouch: true, shouldValidate: true })
                  }}
                  disabled={isRegistering || socialLoading !== null}
                  className="pl-10"
                  style={{
                    '--ring': primaryColor
                  } as React.CSSProperties}
                />
              </div>
              {errors.phone && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-destructive"
                >
                  {errors.phone.message}
                </motion.p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="email" className="text-sm font-medium">
                E-mail
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  {...register('email')}
                  disabled={isRegistering || socialLoading !== null}
                  className="pl-10"
                  style={{
                    '--ring': primaryColor
                  } as React.CSSProperties}
                />
              </div>
              {errors.email && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-destructive"
                >
                  {errors.email.message}
                </motion.p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="password" className="text-sm font-medium">
                Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  {...register('password')}
                  disabled={isRegistering || socialLoading !== null}
                  className="pl-10 pr-10"
                  style={{
                    '--ring': primaryColor
                  } as React.CSSProperties}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">A senha deve conter no mínimo 6 caracteres.</p>
              {errors.password && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-destructive"
                >
                  {errors.password.message}
                </motion.p>
              )}
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-lg bg-destructive/10 p-4 ring-1 ring-destructive/20"
              >
                <p className="text-sm text-destructive">{error}</p>
              </motion.div>
            )}

            <Button
              type="submit"
              className="w-full shadow-md"
              style={{
                backgroundColor: primaryColor,
                borderColor: primaryColor,
                boxShadow: `0 4px 14px 0 ${primaryColor}33`
              }}
              size="lg"
              disabled={isRegistering || socialLoading !== null}
            >
              {isRegistering ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cadastrando...
                </>
              ) : (
                'Cadastrar'
              )}
            </Button>

            <p className="text-center text-xs text-muted-foreground">
              Ao continuar, você concorda com os termos de uso da IgrejaDigital.
            </p>

            <p className="text-center text-sm text-muted-foreground">
              Já possui uma conta?{' '}
              <Link 
                to="/login" 
                className="font-medium hover:underline"
                style={{ color: primaryColor }}
              >
                Voltar para o login
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </motion.div>
  )
}
